import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Check, CheckCircle2, Clock, ExternalLink, List, MessageSquare, RotateCcw, Scale, Send, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Table } from '../components/ui/table';
import DisputeOpsPanel from '../components/DisputeOpsPanel';
import { useApiConfig } from '../hooks/useApiConfig';
import { BACKEND_BASE_URL } from '../config/backendConfig';
import { fetchDisputeOpsQueue } from '../utils/api';
import { formatIssueType } from '../utils/disputeFormat';

const HOUR_MS = 60 * 60 * 1000;

const statusAgeHours = (row) => {
  const enteredAt = new Date(row.statusEnteredAt || row.submittedAt).getTime();
  return Number.isFinite(enteredAt) ? Math.max(0, (Date.now() - enteredAt) / HOUR_MS) : 0;
};

const workspaceKey = (row) => `${row.brand || 'Unknown client'}|||${row.platform || 'Unknown platform'}`;
const formatAge = (hours) => hours < 24 ? `${Math.max(0, Math.floor(hours))}h` : `${(hours / 24).toFixed(1)}d`;

const WORK_TYPES = [
  {
    key: 'RAISE',
    label: 'Tickets to raise',
    shortLabel: 'Raise ticket',
    helper: 'Create on portal',
    icon: ExternalLink,
    tone: 'raise',
    matches: (row) => row.status === 'DISPUTE_PENDING',
  },
  {
    key: 'FOLLOW_UP',
    label: 'Portal checks / follow-ups',
    shortLabel: 'Check / follow up',
    helper: 'Overdue after 2 days',
    icon: Clock,
    tone: 'followup',
    // Every ticket posted to the platform lives here — the 48h threshold
    // only affects how urgent it's shown (see dueMeta below), not whether
    // it's included at all. Matching only the 48h+ subset was why a ticket
    // just marked "posted on portal" vanished from every view for 2 days.
    matches: (row) => row.status === 'AWAITING_PLATFORM_RESPONSE',
  },
  {
    key: 'POST',
    label: 'Responses to post',
    shortLabel: 'Post response',
    helper: 'Brand has replied',
    icon: Send,
    tone: 'post',
    matches: (row) => row.status === 'QUEUED_FOR_UPDATE',
  },
  {
    key: 'WAITING_BRAND',
    label: 'Waiting on brand',
    shortLabel: 'Waiting on brand',
    helper: 'No action needed yet',
    icon: MessageSquare,
    tone: 'waiting',
    matches: (row) => row.status === 'AWAITING_BRAND_RESPONSE',
  },
  {
    key: 'CLOSED',
    label: 'Closed',
    shortLabel: 'Closed',
    helper: 'Resolved disputes',
    icon: Check,
    tone: 'closed',
    matches: (row) => row.status === 'DONE',
  },
];

const getWorkType = (row) => WORK_TYPES.find(type => type.matches(row));
const ALL_WORK_TYPE = {
  key: 'ALL',
  label: 'All pending tickets',
  shortLabel: 'Open ticket',
  tone: 'all',
  matches: (row) => !!getWorkType(row),
};

const dueMeta = (row, workType) => {
  const age = statusAgeHours(row);
  if (workType === 'FOLLOW_UP') {
    const remaining = 48 - age;
    return remaining > 0
      ? { text: `Check in ${formatAge(remaining)}`, overdue: false }
      : { text: `${formatAge(Math.abs(remaining))} past follow-up`, overdue: true };
  }
  // No deadline concept applies once there's nothing for ops to act on —
  // without this, a ticket closed weeks ago would show as wildly "overdue".
  if (workType === 'WAITING_BRAND') return { text: 'No deadline', overdue: false };
  if (workType === 'CLOSED') return { text: 'Closed', overdue: false };
  const remaining = 24 - age;
  return remaining > 0
    ? { text: `Due in ${formatAge(remaining)}`, overdue: false }
    : { text: `${formatAge(Math.abs(remaining))} overdue`, overdue: true };
};

const dueHours = (row) => {
  const workType = getWorkType(row);
  if (workType?.key === 'WAITING_BRAND' || workType?.key === 'CLOSED') return Infinity;
  return (workType?.key === 'FOLLOW_UP' ? 48 : 24) - statusAgeHours(row);
};

export default function DisputeConsolePage() {
  const cfg = useApiConfig();
  const [queue, setQueue] = useState(null);
  const [error, setError] = useState(null);
  const [workspaceSearch, setWorkspaceSearch] = useState('');
  const [workspaceClient, setWorkspaceClient] = useState('ALL');
  const [workspacePlatform, setWorkspacePlatform] = useState('ALL');
  const [workspaceWorkType, setWorkspaceWorkType] = useState('ALL');
  const [workspaceSort, setWorkspaceSort] = useState('TOTAL_DESC');
  const [focusedQueue, setFocusedQueue] = useState(null);
  const [ticketSearch, setTicketSearch] = useState('');
  const [ticketIssue, setTicketIssue] = useState('ALL');
  const [ticketWorkType, setTicketWorkType] = useState('ALL');
  const [ticketDeadline, setTicketDeadline] = useState('ALL');
  const [ticketSort, setTicketSort] = useState('PRIORITY');
  const [sessionIds, setSessionIds] = useState([]);
  const [activeDisputeId, setActiveDisputeId] = useState(null);
  const focusedQueueRef = useRef(null);

  const load = useCallback(async () => {
    if (!cfg) return;
    setError(null);
    try {
      setQueue(await fetchDisputeOpsQueue(BACKEND_BASE_URL, cfg, {}));
    } catch (e) {
      setError(e.message);
    }
  }, [cfg]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => queue?.rows || [], [queue]);
  const counts = useMemo(() => Object.fromEntries(WORK_TYPES.map(type => [type.key, rows.filter(type.matches).length])), [rows]);
  const totalWork = WORK_TYPES.reduce((sum, type) => sum + counts[type.key], 0);

  const workspaceRows = useMemo(() => {
    const map = new Map();
    rows.forEach(row => {
      const type = getWorkType(row);
      if (!type) return;
      const key = workspaceKey(row);
      if (!map.has(key)) {
        map.set(key, {
          key,
          brand: row.brand || 'Unknown client',
          clientId: row.clientId,
          platform: row.platform || 'Unknown platform',
          counts: Object.fromEntries(WORK_TYPES.map(item => [item.key, 0])),
        });
      }
      map.get(key).counts[type.key] += 1;
    });
    return [...map.values()]
      .map(item => ({ ...item, total: WORK_TYPES.reduce((sum, type) => sum + item.counts[type.key], 0) }));
  }, [rows]);

  const workspaceClients = useMemo(() => [...new Set(workspaceRows.map(item => item.brand))].sort(), [workspaceRows]);
  const workspacePlatforms = useMemo(() => [...new Set(workspaceRows.map(item => item.platform))].sort(), [workspaceRows]);

  const visibleWorkspaces = useMemo(() => {
    const query = workspaceSearch.trim().toLowerCase();
    return workspaceRows.filter(item => {
      if (query && !`${item.brand} ${item.platform}`.toLowerCase().includes(query)) return false;
      if (workspaceClient !== 'ALL' && item.brand !== workspaceClient) return false;
      if (workspacePlatform !== 'ALL' && item.platform !== workspacePlatform) return false;
      if (workspaceWorkType !== 'ALL' && item.counts[workspaceWorkType] === 0) return false;
      return true;
    }).sort((a, b) => {
      if (workspaceSort === 'CLIENT_ASC') return a.brand.localeCompare(b.brand) || a.platform.localeCompare(b.platform);
      if (workspaceSort === 'PLATFORM_ASC') return a.platform.localeCompare(b.platform) || a.brand.localeCompare(b.brand);
      if (workspaceSort.startsWith('WORK_')) {
        const typeKey = workspaceSort.replace('WORK_', '').replace('_DESC', '');
        return b.counts[typeKey] - a.counts[typeKey] || b.total - a.total || a.brand.localeCompare(b.brand);
      }
      return b.total - a.total || `${a.brand}${a.platform}`.localeCompare(`${b.brand}${b.platform}`);
    });
  }, [workspaceRows, workspaceSearch, workspaceClient, workspacePlatform, workspaceWorkType, workspaceSort]);

  const visibleWorkspaceTotals = useMemo(() => Object.fromEntries([
    ...WORK_TYPES.map(type => [type.key, visibleWorkspaces.reduce((sum, item) => sum + item.counts[type.key], 0)]),
    ['ALL', visibleWorkspaces.reduce((sum, item) => sum + item.total, 0)],
  ]), [visibleWorkspaces]);

  const focusedType = focusedQueue
    ? (focusedQueue.type === 'ALL' ? ALL_WORK_TYPE : WORK_TYPES.find(type => type.key === focusedQueue.type))
    : null;
  const focusedBaseRows = useMemo(() => {
    if (!focusedQueue || !focusedType) return [];
    return rows.filter(row => {
      const inWorkspace = focusedQueue.workspace === 'ALL' || workspaceKey(row) === focusedQueue.workspace;
      return inWorkspace && focusedType.matches(row);
    });
  }, [rows, focusedQueue, focusedType]);

  const ticketIssues = useMemo(() => [...new Set(focusedBaseRows.map(row => row.issueType).filter(Boolean))]
    .sort((a, b) => formatIssueType(a).localeCompare(formatIssueType(b))), [focusedBaseRows]);

  const focusedRows = useMemo(() => {
    const query = ticketSearch.trim().toLowerCase();
    const workspaceTotals = new Map(workspaceRows.map(item => [item.key, item.total]));
    return focusedBaseRows.filter(row => {
      const rowType = getWorkType(row);
      if (query && !`${row.invoiceNumber || ''} ${row.customerLegalName || ''} ${row.platformTicketNumber || ''} ${row.brand || ''} ${row.platform || ''}`.toLowerCase().includes(query)) return false;
      if (ticketIssue !== 'ALL' && row.issueType !== ticketIssue) return false;
      if (ticketWorkType !== 'ALL' && rowType?.key !== ticketWorkType) return false;
      const remaining = dueHours(row);
      if (ticketDeadline === 'OVERDUE' && remaining >= 0) return false;
      if (ticketDeadline === 'DUE_SOON' && (remaining < 0 || remaining > 8)) return false;
      if (ticketDeadline === 'ON_TRACK' && remaining <= 8) return false;
      return true;
    }).sort((a, b) => {
      if (ticketSort === 'AGE_DESC') return statusAgeHours(b) - statusAgeHours(a);
      if (ticketSort === 'AGE_ASC') return statusAgeHours(a) - statusAgeHours(b);
      if (ticketSort === 'INVOICE_ASC') return String(a.invoiceNumber || '').localeCompare(String(b.invoiceNumber || ''));
      if (ticketSort === 'CLIENT_ASC') return String(a.brand || '').localeCompare(String(b.brand || '')) || String(a.platform || '').localeCompare(String(b.platform || ''));
      if (ticketSort === 'PLATFORM_ASC') return String(a.platform || '').localeCompare(String(b.platform || '')) || String(a.brand || '').localeCompare(String(b.brand || ''));
      if (focusedQueue.workspace === 'ALL') {
        const totalDifference = (workspaceTotals.get(workspaceKey(b)) || 0) - (workspaceTotals.get(workspaceKey(a)) || 0);
        if (totalDifference) return totalDifference;
        const workspaceDifference = workspaceKey(a).localeCompare(workspaceKey(b));
        if (workspaceDifference) return workspaceDifference;
      }
      return dueHours(a) - dueHours(b);
    });
  }, [focusedBaseRows, workspaceRows, focusedQueue, ticketSearch, ticketIssue, ticketWorkType, ticketDeadline, ticketSort]);
  const showAllWorkspaces = focusedQueue?.workspace === 'ALL';
  const hasWorkspaceFilters = !!workspaceSearch || workspaceClient !== 'ALL' || workspacePlatform !== 'ALL' || workspaceWorkType !== 'ALL';
  const hasTicketFilters = !!ticketSearch || ticketIssue !== 'ALL' || ticketWorkType !== 'ALL' || ticketDeadline !== 'ALL';

  useEffect(() => {
    if (focusedQueue) focusedQueueRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focusedQueue]);

  const selectWorkQueue = (workspace, type) => {
    setFocusedQueue({ workspace: workspace.key, brand: workspace.brand, platform: workspace.platform, type: type.key });
    setTicketSearch('');
    setTicketIssue('ALL');
    setTicketWorkType('ALL');
    setTicketDeadline('ALL');
    setTicketSort('PRIORITY');
    setActiveDisputeId(null);
    setSessionIds([]);
  };

  const selectAllWork = () => {
    setFocusedQueue({ workspace: 'ALL', brand: 'All clients', platform: 'All portals', type: 'ALL' });
    setTicketSearch('');
    setTicketIssue('ALL');
    setTicketWorkType('ALL');
    setTicketDeadline('ALL');
    setTicketSort('PRIORITY');
    setActiveDisputeId(null);
    setSessionIds([]);
  };

  const clearWorkspaceFilters = () => {
    setWorkspaceSearch('');
    setWorkspaceClient('ALL');
    setWorkspacePlatform('ALL');
    setWorkspaceWorkType('ALL');
  };

  const clearTicketFilters = () => {
    setTicketSearch('');
    setTicketIssue('ALL');
    setTicketWorkType('ALL');
    setTicketDeadline('ALL');
  };

  const startSession = (startIndex = 0) => {
    const ids = focusedRows.map(row => row.disputeId);
    if (!ids.length) return;
    setSessionIds(ids);
    setActiveDisputeId(ids[startIndex]);
  };

  if (!cfg) return null;

  return (
    <div className="page-content disp-page disp-ops-workbench">
      <div className="section-header">
        <Scale size={18} />
        <div><h2>Dispute Console</h2><p>Today’s operational workload across client portals</p></div>
        <div className="header-right"><Button variant="outline" size="sm" onClick={load}><RotateCcw size={15} />Refresh</Button></div>
      </div>

      {error && <p className="error-msg">{error}</p>}
      {!queue && !error && <div className="loading-state"><div className="spinner" /><p>Loading today’s work…</p></div>}

      {queue && (
        <>
          <section className="disp-today" aria-labelledby="today-work-title">
            <div className="disp-block-title"><div><span>Daily workload</span><h3 id="today-work-title">What needs to be completed today</h3></div><small>Includes tickets waiting on the brand and already-closed disputes, which need no action from you</small></div>
            <div className="disp-today-cards">
              <div className="disp-today-card total"><div className="disp-today-icon"><CheckCircle2 size={18} /></div><div><strong>{totalWork}</strong><span>Total tickets in queue</span></div></div>
              {WORK_TYPES.map(type => {
                const Icon = type.icon;
                return <div key={type.key} className={`disp-today-card ${type.tone}`}><div className="disp-today-icon"><Icon size={18} /></div><div><strong>{counts[type.key]}</strong><span>{type.label}</span><small>{type.helper}</small></div></div>;
              })}
            </div>
          </section>

          <section className="disp-work-matrix" aria-labelledby="work-matrix-title">
            <div className="disp-block-title">
              <div><span>Portal workspaces</span><h3 id="work-matrix-title">Choose a client and portal to work on</h3></div>
              <div className="disp-matrix-controls">
                <Input className="search-input" placeholder="Search client or platform…" value={workspaceSearch} onChange={e => setWorkspaceSearch(e.target.value)} />
                <Button variant="outline" size="sm" disabled={!totalWork} onClick={selectAllWork}><List size={14} /> View all {totalWork} tickets</Button>
              </div>
            </div>

            <div className="disp-table-controls" aria-label="Workspace table filters and sorting">
              <label className="disp-control"><span>Client</span><select value={workspaceClient} onChange={e => setWorkspaceClient(e.target.value)}><option value="ALL">All clients</option>{workspaceClients.map(client => <option key={client} value={client}>{client}</option>)}</select></label>
              <label className="disp-control"><span>Platform</span><select value={workspacePlatform} onChange={e => setWorkspacePlatform(e.target.value)}><option value="ALL">All platforms</option>{workspacePlatforms.map(platform => <option key={platform} value={platform}>{platform}</option>)}</select></label>
              <label className="disp-control"><span>Work required</span><select value={workspaceWorkType} onChange={e => setWorkspaceWorkType(e.target.value)}><option value="ALL">Any work type</option>{WORK_TYPES.map(type => <option key={type.key} value={type.key}>{type.label}</option>)}</select></label>
              <label className="disp-control disp-sort-control"><span>Sort by</span><select value={workspaceSort} onChange={e => setWorkspaceSort(e.target.value)}><option value="TOTAL_DESC">Total work: high to low</option><option value="WORK_RAISE_DESC">Tickets to raise: high to low</option><option value="WORK_FOLLOW_UP_DESC">Follow-ups: high to low</option><option value="WORK_POST_DESC">Responses to post: high to low</option><option value="WORK_WAITING_BRAND_DESC">Waiting on brand: high to low</option><option value="WORK_CLOSED_DESC">Closed: high to low</option><option value="CLIENT_ASC">Client: A to Z</option><option value="PLATFORM_ASC">Platform: A to Z</option></select></label>
              {hasWorkspaceFilters && <Button variant="ghost" size="sm" onClick={clearWorkspaceFilters}>Clear filters</Button>}
              <span className="disp-result-count">{visibleWorkspaces.length} of {workspaceRows.length} workspaces</span>
            </div>

            <div className="table-wrap disp-matrix-wrap">
              <Table className="users-table disp-matrix">
                <thead><tr><th>Client</th><th>Platform</th><th><span>Total</span><small>{workspaceSort === 'TOTAL_DESC' ? 'Sorted high to low' : 'Pending work'}</small></th>{WORK_TYPES.map(type => <th key={type.key}><span>{type.label}</span><small>{type.helper}</small></th>)}</tr></thead>
                <tbody>
                  {visibleWorkspaces.length === 0 && <tr><td colSpan={6}><div className="empty-state">No pending work matches this search.</div></td></tr>}
                  {visibleWorkspaces.map(workspace => (
                    <tr key={workspace.key}>
                      <td><strong>{workspace.brand}</strong>{workspace.clientId != null && <small className="disp-muted"> #{workspace.clientId}</small>}</td>
                      <td><span className="client-chip">{workspace.platform}</span></td>
                      <td className="disp-matrix-all">
                        <button onClick={() => selectWorkQueue(workspace, ALL_WORK_TYPE)} aria-label={`View all ${workspace.total} pending tickets for ${workspace.brand} on ${workspace.platform}`}><strong>{workspace.total}</strong><ArrowRight size={12} /></button>
                      </td>
                      {WORK_TYPES.map(type => (
                        <td key={type.key} className={`disp-matrix-task ${type.tone}`}>
                          {workspace.counts[type.key] > 0
                            ? <button onClick={() => selectWorkQueue(workspace, type)} aria-label={`${workspace.counts[type.key]} ${type.label} for ${workspace.brand} on ${workspace.platform}`}><strong>{workspace.counts[type.key]}</strong><span>View</span></button>
                            : <span className="disp-matrix-zero">—</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr><td colSpan={2}>{hasWorkspaceFilters ? 'Visible pending work' : 'Total pending work'}</td><td>{visibleWorkspaceTotals.ALL}</td>{WORK_TYPES.map(type => <td key={type.key}>{visibleWorkspaceTotals[type.key]}</td>)}</tr></tfoot>
              </Table>
            </div>
          </section>

          {focusedQueue && focusedType && (
            <section ref={focusedQueueRef} className="disp-focused-queue" aria-labelledby="focused-queue-title">
              <div className="disp-focused-head">
                <div><span>{focusedQueue.brand} <b>·</b> {focusedQueue.platform}</span><h3 id="focused-queue-title">{focusedType.label}</h3><p>{focusedBaseRows.length} {focusedBaseRows.length === 1 ? 'ticket' : 'tickets'} in this portal session</p></div>
                <div className="disp-focused-actions">
                  <Button variant="ghost" size="sm" onClick={() => setFocusedQueue(null)}><X size={14} /> Close list</Button>
                  <Button size="sm" disabled={!focusedRows.length} onClick={() => startSession(0)}>Start with first ticket <ArrowRight size={14} /></Button>
                </div>
              </div>

              <div className="disp-table-controls disp-ticket-controls" aria-label="Ticket table filters and sorting">
                <Input className="search-input" aria-label="Search invoice, entity or platform ticket" placeholder="Search invoice, entity or ticket number" value={ticketSearch} onChange={e => setTicketSearch(e.target.value)} />
                <label className="disp-control"><span>Issue</span><select value={ticketIssue} onChange={e => setTicketIssue(e.target.value)}><option value="ALL">All issue types</option>{ticketIssues.map(issue => <option key={issue} value={issue}>{formatIssueType(issue)}</option>)}</select></label>
                {focusedType.key === 'ALL' && <label className="disp-control"><span>Work required</span><select value={ticketWorkType} onChange={e => setTicketWorkType(e.target.value)}><option value="ALL">Any work type</option>{WORK_TYPES.map(type => <option key={type.key} value={type.key}>{type.label}</option>)}</select></label>}
                <label className="disp-control"><span>Deadline</span><select value={ticketDeadline} onChange={e => setTicketDeadline(e.target.value)}><option value="ALL">Any deadline</option><option value="OVERDUE">Overdue</option><option value="DUE_SOON">Due within 8 hours</option><option value="ON_TRACK">More than 8 hours left</option></select></label>
                <label className="disp-control disp-sort-control"><span>Sort by</span><select value={ticketSort} onChange={e => setTicketSort(e.target.value)}><option value="PRIORITY">Recommended work order</option><option value="AGE_DESC">Age: oldest first</option><option value="AGE_ASC">Age: newest first</option><option value="INVOICE_ASC">Invoice: A to Z</option>{showAllWorkspaces && <><option value="CLIENT_ASC">Client: A to Z</option><option value="PLATFORM_ASC">Platform: A to Z</option></>}</select></label>
                {hasTicketFilters && <Button variant="ghost" size="sm" onClick={clearTicketFilters}>Clear filters</Button>}
                <span className="disp-result-count">{focusedRows.length} of {focusedBaseRows.length} tickets</span>
              </div>

              <div className="table-wrap disp-focused-wrap">
                <Table className="users-table disp-focused-table">
                  <thead><tr><th>#</th>{showAllWorkspaces && <><th>Client</th><th>Platform</th></>}<th>Invoice</th><th>Entity name</th><th>Issue</th><th>Work type</th><th>Platform ticket</th><th>Age in status</th><th>Deadline</th><th className="disp-r">Work on ticket</th></tr></thead>
                  <tbody>
                    {focusedRows.length === 0 && <tr><td colSpan={showAllWorkspaces ? 11 : 9}><div className="empty-state">{focusedBaseRows.length ? 'No tickets match the selected filters.' : 'This queue is complete.'}</div></td></tr>}
                    {focusedRows.map((row, index) => {
                      const rowType = getWorkType(row);
                      const due = dueMeta(row, rowType.key);
                      return (
                        <tr key={row.disputeId} className="disp-row" onClick={() => startSession(index)}>
                          <td className="disp-muted">{index + 1}</td>
                          {showAllWorkspaces && <><td><strong>{row.brand}</strong>{row.clientId != null && <small className="disp-muted"> #{row.clientId}</small>}</td><td><span className="client-chip">{row.platform}</span></td></>}
                          <td className="disp-mono">{row.invoiceNumber}</td>
                          <td className={!row.customerLegalName ? 'disp-muted' : ''}>{row.customerLegalName || '—'}</td>
                          <td>{formatIssueType(row.issueType)}</td>
                          <td><span className={`disp-work-chip ${rowType.tone}`}>{rowType.shortLabel}</span></td>
                          <td className={`disp-mono ${!row.platformTicketNumber ? 'disp-tk-none' : ''}`}>{row.platformTicketNumber || 'Not raised'}</td>
                          <td><span className="disp-age"><Clock size={11} /> {formatAge(statusAgeHours(row))}</span></td>
                          <td><span className={`disp-queue-due ${due.overdue ? 'overdue' : ''}`}>{due.text}</span></td>
                          <td className="disp-r"><button className={`disp-tbl-do ${rowType.tone === 'post' ? 'post' : 'hot'}`} onClick={(event) => { event.stopPropagation(); startSession(index); }}>{rowType.shortLabel}</button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </section>
          )}
        </>
      )}

      <DisputeOpsPanel
        disputeId={activeDisputeId}
        cfg={cfg}
        workQueueIds={sessionIds}
        onSelectDispute={setActiveDisputeId}
        onClose={() => setActiveDisputeId(null)}
        onChanged={load}
      />
    </div>
  );
}
